import type {
  ContainsFilter,
  ExactFilter,
  TriggerAction,
  TriggerAudience,
  TriggerFilter,
  TriggerRule,
  WholeWordFilter
} from "./types"

export type TriggerActionKind = TriggerAction["action_type"]

/** "contains" is how the AI and the API store auto-replies; the editor keeps it on save. */
export type TriggerMatchMode = "whole_word" | "contains"

const EDITABLE_PUNISHMENT_KINDS: ReadonlySet<string> = new Set(["mute", "ban", "kick", "clean"])
// An admin's reply also gives one /warn warning; it never carries a counter.
const REPLY_PUNISHMENT_KINDS: ReadonlySet<string> = new Set([...EDITABLE_PUNISHMENT_KINDS, "warning"])

export const audienceOf = (rule: TriggerRule): TriggerAudience => rule.audience ?? "anyone"

const leavesOf = (filter: TriggerFilter): TriggerFilter[] => (filter.filter_type === "or" ? filter.filters : [filter])

const isContainsLeaf = (filter: TriggerFilter): filter is ContainsFilter => filter.filter_type === "contains"

const isWholeWordLeaf = (filter: TriggerFilter): filter is WholeWordFilter => filter.filter_type === "whole_word"

const isExactFilter = (filter: TriggerFilter): filter is ExactFilter => filter.filter_type === "exact"

/** The shapes one word list can stand for without dropping a stored flag; the rest stays read-only. */
const isEditableFilter = (filter: TriggerFilter): boolean => {
  const leaves = leavesOf(filter)
  if (leaves.length === 0) return false
  if (leaves.length === 1 && isWholeWordLeaf(leaves[0])) return true
  if (!leaves.every(isContainsLeaf)) return false
  const caseSensitive = leaves[0].case_sensitive ?? false
  return leaves.every(leaf => (leaf.case_sensitive ?? false) === caseSensitive)
}

const isEditableAction = (action: TriggerAction): boolean => {
  if (action.action_type !== "punishment") return true
  const { punishment_kind, warning_threshold, warning_observation_interval } = action.parameters
  if (!EDITABLE_PUNISHMENT_KINDS.has(punishment_kind)) return false
  // The warning step needs a window and a mute, kick or ban after it; anything else would be rewritten on save.
  if (warning_threshold === null) return true
  return warning_observation_interval !== null && punishment_kind !== "clean"
}

/** What the backend accepts on an admin's reply: a bare exact filter and one punishment without a counter. */
const isEditableReplyTrigger = (rule: TriggerRule): boolean => {
  if (!isExactFilter(rule.filter) || rule.actions.length !== 1) return false
  const [action] = rule.actions
  if (action.action_type !== "punishment") return false
  const { punishment_kind, warning_threshold, warning_observation_interval } = action.parameters
  return (
    REPLY_PUNISHMENT_KINDS.has(punishment_kind) && warning_threshold === null && warning_observation_interval === null
  )
}

/** What the backend accepts from an admin's own message: a bare exact filter and one non-punishment action. */
const isEditableAdminsTrigger = (rule: TriggerRule): boolean =>
  isExactFilter(rule.filter) && rule.actions.length === 1 && rule.actions[0].action_type !== "punishment"

/** The words of an editable rule, or null for regex and compound filters. */
export const triggerWordsOf = (filter: TriggerFilter): string[] | null => {
  if (isExactFilter(filter)) return filter.words
  const leaves = leavesOf(filter)
  if (!leaves.every(leaf => isWholeWordLeaf(leaf) || isContainsLeaf(leaf))) return null
  return leaves.flatMap(leaf => (isWholeWordLeaf(leaf) ? leaf.words : isContainsLeaf(leaf) ? [leaf.substring] : []))
}

export const actionKindOf = (rule: TriggerRule): TriggerActionKind | null =>
  rule.actions.length === 1 ? rule.actions[0].action_type : null

export const actionKindsOf = (rule: TriggerRule): TriggerActionKind[] => rule.actions.map(action => action.action_type)

export const isEditableTrigger = (rule: TriggerRule): boolean => {
  switch (audienceOf(rule)) {
    case "anyone":
      return (
        isEditableFilter(rule.filter) &&
        rule.actions.every(isEditableAction) &&
        (rule.actions.length === 1 ||
          (rule.actions.length === 2 &&
            actionKindsOf(rule).includes("punishment") &&
            actionKindsOf(rule).includes("send_message")))
      )
    case "admin_reply":
      return isEditableReplyTrigger(rule)
    case "admins":
      return isEditableAdminsTrigger(rule)
  }
}

export const matchModeOf = (filter: TriggerFilter): TriggerMatchMode =>
  leavesOf(filter).some(isContainsLeaf) ? "contains" : "whole_word"

/** The stored flag as is: undefined means the rule predates the switch. */
export const storedAntiEvasionOf = (filter: TriggerFilter): boolean | undefined =>
  leavesOf(filter).find(isWholeWordLeaf)?.anti_evasion

// Lists saved before the switch existed were already matched with anti-evasion, so absent means on.
export const antiEvasionOf = (filter: TriggerFilter): boolean => storedAntiEvasionOf(filter) ?? true

export const caseSensitiveOf = (filter: TriggerFilter): boolean =>
  leavesOf(filter).find(
    (leaf): leaf is WholeWordFilter | ContainsFilter => isWholeWordLeaf(leaf) || isContainsLeaf(leaf)
  )?.case_sensitive ?? false

interface BuildTriggerFilterOptions {
  matchMode: TriggerMatchMode
  caseSensitive: boolean
  /** Omitted keeps the backend default and the stored absence. */
  antiEvasion?: boolean
}

export const buildExactFilter = (words: readonly string[]): ExactFilter => ({ filter_type: "exact", words: [...words] })

export const buildTriggerFilter = (
  words: readonly string[],
  { matchMode, caseSensitive, antiEvasion }: BuildTriggerFilterOptions
): TriggerFilter => {
  if (matchMode === "contains")
    return {
      filter_type: "or",
      filters: words.map(substring => ({ filter_type: "contains", substring, case_sensitive: caseSensitive }))
    }
  return {
    filter_type: "or",
    filters: [
      {
        filter_type: "whole_word",
        words: [...words],
        case_sensitive: caseSensitive,
        ...(antiEvasion === undefined ? {} : { anti_evasion: antiEvasion })
      }
    ]
  }
}

/** Leaf words or patterns of any filter, for rows the editor cannot open. */
export const filterLabelsOf = (filter: TriggerFilter): string[] => {
  switch (filter.filter_type) {
    case "whole_word":
    case "exact":
      return filter.words
    case "contains":
      return [filter.substring]
    case "regex":
      return [filter.pattern]
    case "not":
      return filterLabelsOf(filter.filter)
    case "and":
    case "or":
      return filter.filters.flatMap(filterLabelsOf)
  }
}
